import React from 'react'
import { CircleHelp, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../../../../tailwindComponents/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../tailwindComponents/ui/tooltip'
import { useV3ShellLayout } from '../../../../components/layout/V3ShellLayoutContext'
import { cn } from '../../../../lib/utils'
import {
  createEmptyDashboardFilterSelection,
  createEmptyDashboardFilters,
  DashboardFilterOption,
  DashboardFilterSelection,
  DashboardFilterState,
  getDashboardFilterOptionLabel,
  getDashboardFilterSelectionLabel,
  getScopedDashboardClassOptions,
  hasActiveDashboardFilters,
  normalizeDashboardFilterSelection,
} from '../../../../services/dashboards/dashboardFilters'

function DisabledFiltersTooltip({ tooltipText }: { tooltipText: string }) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-[#7F56D9] hover:bg-[#F9F5FF] hover:text-[#6941C6] focus:outline-none transition-colors"
          >
            <CircleHelp className="h-4 w-4 shrink-0 text-[#9E77ED]" strokeWidth={2} />
            <span>Why disabled?</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          className="z-50 max-w-[320px] rounded-xl border border-[#344054] bg-[#344054] px-4 py-3 text-xs leading-relaxed text-white shadow-xl"
        >
          <p className="font-normal">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function ChevronDown() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
      <path d="M5 7.5L10 12.5L15 7.5" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CheckIcon({ stroke = '#7F56D9' }: { stroke?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M10 3L4.5 8.5L2 6" stroke={stroke} strokeWidth="1.6666" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ItemCheckbox({ checked, accent = 'purple' }: { checked: boolean; accent?: 'purple' | 'red' }) {
  const checkedClasses = accent === 'red'
    ? 'border-[#F97066] bg-[#FEF3F2]'
    : 'border-[#7F56D9] bg-[#F9F5FF]'
  const checkStroke = accent === 'red' ? '#D92D20' : '#7F56D9'

  return (
    <div className={cn(
      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
      checked ? checkedClasses : 'border-[#98A2B3] bg-white',
    )}>
      {checked && <CheckIcon stroke={checkStroke} />}
    </div>
  )
}

function FilterDropdown({
  title,
  placeholder,
  options,
  selection,
  showExclude = false,
  disabled = false,
  disabledTooltip,
  pluralLabel,
  onApply,
}: {
  title: string;
  placeholder: string;
  options: DashboardFilterOption[];
  selection: DashboardFilterSelection;
  showExclude?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  pluralLabel?: string;
  onApply: (selection: DashboardFilterSelection) => void;
}) {
  const [open, setOpen] = React.useState(false)
  const [draftSelection, setDraftSelection] = React.useState<DashboardFilterSelection>(normalizeDashboardFilterSelection(selection))

  React.useEffect(() => {
    if (open) setDraftSelection(normalizeDashboardFilterSelection(selection))
  }, [open, selection])

  const normalizedSelection = normalizeDashboardFilterSelection(selection)
  const isActive = normalizedSelection.selected.length > 0
  const isExclude = isActive && normalizedSelection.exclude
  const triggerLabel = getDashboardFilterSelectionLabel(normalizedSelection, options, placeholder, pluralLabel)

  const toggleOption = (optionCode: string) => {
    setDraftSelection((previous) => {
      const alreadySelected = previous.selected.includes(optionCode)
      const selected = alreadySelected
        ? previous.selected.filter((item) => item !== optionCode)
        : [...previous.selected, optionCode]

      return {
        selected,
        exclude: selected.length > 0 ? previous.exclude : false,
      }
    })
  }

  const handleReset = () => setDraftSelection({ selected: [], exclude: false })

  return (
    <Popover open={open} onOpenChange={(nextOpen) => !disabled && setOpen(nextOpen)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={disabled && disabledTooltip ? disabledTooltip : undefined}
          className={cn(
            'inline-flex items-center justify-between gap-2 rounded-xl border bg-white px-3.5 py-2 text-xs font-semibold shadow-2xs transition-colors',
            disabled
              ? 'border-gray-200 bg-gray-50/70 text-gray-400 cursor-not-allowed opacity-75'
              : isActive
                ? isExclude
                  ? 'border-[#F97066] bg-[#FEF3F2] text-[#B42318] hover:bg-[#FEE4E2]'
                  : 'border-[#7F56D9] bg-[#F9F5FF] text-[#6941C6] hover:bg-[#F4EBFF]'
                : 'border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]',
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="w-[300px] overflow-hidden rounded-lg border border-[#EAECF0] bg-white p-0 shadow-[0_12px_16px_-4px_rgba(0,0,0,0.08),0_4px_6px_-2px_rgba(0,0,0,0.03)]">
        <div className="px-4 py-3">
          <span className="text-sm font-semibold text-[#344054]">{title}</span>
        </div>
        <div className="h-px w-full bg-[#F5F5F5]" />

        <div className="flex max-h-[280px] flex-col overflow-auto">
          {options.map((option) => {
            const checked = draftSelection.selected.includes(option.code)
            return (
              <button
                key={option.code}
                type="button"
                onClick={() => toggleOption(option.code)}
                className="flex items-center gap-3 px-4 py-2.5 text-left hover:bg-secondary/60"
              >
                <ItemCheckbox checked={checked} />
                <span className="flex-1 truncate text-sm text-[#344054]">{getDashboardFilterOptionLabel(option)}</span>
              </button>
            )
          })}
          {options.length === 0 && <div className="px-4 py-3 text-sm text-[#667085]">No options available.</div>}
        </div>

        {showExclude && (
          <>
            <div className="h-px w-full bg-[#F5F5F5]" />
            <button
              type="button"
              onClick={() => setDraftSelection((previous) => ({
                ...previous,
                exclude: previous.selected.length > 0 ? !previous.exclude : false,
              }))}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 text-left',
                draftSelection.exclude && draftSelection.selected.length > 0 && 'bg-[#FEF3F2]',
              )}
            >
              <ItemCheckbox checked={draftSelection.exclude} accent="red" />
              <span className={cn(
                'text-sm font-medium',
                draftSelection.exclude && draftSelection.selected.length > 0 ? 'text-[#B42318]' : 'text-[#667085]',
              )}>
                Exclude selected instead
              </span>
            </button>
          </>
        )}

        <div className="h-px w-full bg-[#F5F5F5]" />
        <div className="flex items-center justify-between px-4 py-3">
          <button type="button" onClick={handleReset} className="text-xs font-medium text-[#667085] hover:text-[#101828]">
            Reset
          </button>
          <button
            type="button"
            onClick={() => {
              onApply(normalizeDashboardFilterSelection(draftSelection))
              setOpen(false)
            }}
            className="rounded-md bg-[#7F56D9] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#6941C6]"
          >
            Apply
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface DashboardFilterBarProps {
  filters: DashboardFilterState;
  locationOptions: DashboardFilterOption[];
  categoryOptions: DashboardFilterOption[];
  classOptions: DashboardFilterOption[];
  disabled?: boolean;
  disabledTooltip?: string;
  // SPS-5572: disable only specific filters (e.g. category/class on the Health tab, where
  // the score is benchmarked whole-store) while leaving the rest — location — enabled.
  disabledFilters?: Array<keyof DashboardFilterState>;
  fullBleed?: boolean;
  bare?: boolean;
  className?: string;
  onChange: (filters: DashboardFilterState) => void;
}

function normalizeSelectionForAvailableOptions(
  selection: DashboardFilterSelection,
  availableOptions: DashboardFilterOption[],
): DashboardFilterSelection {
  const normalizedSelection = normalizeDashboardFilterSelection(selection)
  const availableCodes = Array.from(new Set(availableOptions.map((option) => option.code).filter(Boolean)))

  if (availableCodes.length === 0) {
    return createEmptyDashboardFilterSelection()
  }

  const allowedCodes = new Set(availableCodes)
  const selected = normalizedSelection.selected.filter((code) => allowedCodes.has(code))

  return {
    selected,
    exclude: selected.length > 0 ? normalizedSelection.exclude : false,
  }
}

export default function DashboardFilterBar({
  filters,
  locationOptions,
  categoryOptions,
  classOptions,
  disabled = false,
  disabledTooltip,
  disabledFilters = [],
  fullBleed = false,
  bare = false,
  className,
  onChange,
}: DashboardFilterBarProps) {
  const { leftNavWidth } = useV3ShellLayout()
  const activeDisabledTooltip = disabledTooltip || "Your Health Score is benchmarked against whole-store data, so category and class filters don’t apply here. Location filtering is supported."
  const isFilterDisabled = (key: keyof DashboardFilterState) => disabled || disabledFilters.includes(key)
  const anyFilterDisabled = disabled || disabledFilters.length > 0
  const scopedClassOptions = React.useMemo(
    () => getScopedDashboardClassOptions(classOptions, filters.category),
    [classOptions, filters.category],
  )
  const fullBleedFilterBarStyle = React.useMemo<React.CSSProperties>(() => (
    leftNavWidth > 0
      ? {
        width: `calc(100vw - ${leftNavWidth}px)`,
        marginLeft: '-45px',
      }
      : {
        width: '100vw',
        marginLeft: '-2.5vw',
      }
  ), [leftNavWidth])

  const [isDisabledTooltipOpen, setIsDisabledTooltipOpen] = React.useState(false)

  const activeChips = React.useMemo(() => {
    const groups: Array<{ key: keyof DashboardFilterState; title: string; selection: DashboardFilterSelection; options: DashboardFilterOption[] }> = [
      { key: 'location', title: 'Location', selection: filters.location, options: locationOptions },
      { key: 'category', title: 'Category', selection: filters.category, options: categoryOptions },
      { key: 'class', title: 'Class', selection: filters.class, options: classOptions },
    ]

    return groups.flatMap(({ key, title, selection, options }) => normalizeDashboardFilterSelection(selection).selected.map((value) => {
      const option = options.find((item) => item.code === value)
      const label = option ? getDashboardFilterOptionLabel(option) : value
      return {
        key,
        value,
        exclude: selection.exclude,
        label: selection.exclude ? `${title}: Not ${label}` : `${title}: ${label}`,
      }
    }))
  }, [categoryOptions, classOptions, filters, locationOptions])

  const updateSelection = (key: keyof DashboardFilterState, selection: DashboardFilterSelection) => {
    const availableOptions = key === 'location'
      ? locationOptions
      : key === 'category'
        ? categoryOptions
        : scopedClassOptions

    const nextFilters = {
      ...filters,
      [key]: normalizeSelectionForAvailableOptions(selection, availableOptions),
    }

    if (key === 'category') {
      const scopedClasses = getScopedDashboardClassOptions(classOptions, nextFilters.category)
      const allowedClassCodes = new Set(scopedClasses.map((option) => option.code))
      const keepExistingClasses = nextFilters.category.exclude || nextFilters.category.selected.length === 0
      nextFilters.class = keepExistingClasses
        ? normalizeDashboardFilterSelection(nextFilters.class)
        : {
          selected: nextFilters.class.selected.filter((code) => allowedClassCodes.has(code)),
          exclude: nextFilters.class.exclude,
        }
      nextFilters.class = normalizeDashboardFilterSelection(nextFilters.class)
    }

    onChange(nextFilters)
  }

  const content = (
    <>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <span className="text-sm font-medium text-[#475467]">Filters:</span>
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <FilterDropdown
              title="Locations"
              placeholder="All Locations"
              options={locationOptions}
              selection={filters.location}
              showExclude
              disabled={isFilterDisabled('location')}
              disabledTooltip={activeDisabledTooltip}
              pluralLabel="locations"
              onApply={(selection) => updateSelection('location', selection)}
            />
            <FilterDropdown
              title="Categories"
              placeholder="All Categories"
              options={categoryOptions}
              selection={filters.category}
              showExclude
              disabled={isFilterDisabled('category')}
              disabledTooltip={activeDisabledTooltip}
              pluralLabel="categories"
              onApply={(selection) => updateSelection('category', selection)}
            />
            <FilterDropdown
              title="Classes"
              placeholder="All Classes"
              options={scopedClassOptions}
              selection={filters.class}
              showExclude
              disabled={isFilterDisabled('class')}
              disabledTooltip={activeDisabledTooltip}
              pluralLabel="classes"
              onApply={(selection) => updateSelection('class', selection)}
            />
            {anyFilterDisabled && (
              <DisabledFiltersTooltip tooltipText={activeDisabledTooltip} />
            )}
          </div>
        </div>
        {hasActiveDashboardFilters(filters) && (
          <button
            type="button"
            onClick={() => onChange(createEmptyDashboardFilters())}
            className="self-start text-xs font-medium text-[#667085] hover:text-[#101828] lg:self-center"
          >
            Reset all
          </button>
        )}
      </div>

      {activeChips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#EAECF0] pt-3">
          {activeChips.map((chip) => (
            <span
              key={`${chip.key}-${chip.value}-${chip.exclude ? 'ex' : 'inc'}`}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border',
                chip.exclude
                  ? 'border-[#FECDCA] bg-[#FEF3F2] text-[#B42318]'
                  : 'border-[#E9D7FE] bg-[#F9F5FF] text-[#6941C6]',
              )}
              data-exclude={chip.exclude ? 'true' : 'false'}
            >
              <span className={cn('text-xs font-medium', chip.exclude ? 'text-[#B42318]' : 'text-[#6941C6]')}>{chip.label}</span>
              <button
                type="button"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => {
                  const currentSelection = normalizeDashboardFilterSelection(filters[chip.key])
                  updateSelection(chip.key, {
                    selected: currentSelection.selected.filter((value) => value !== chip.value),
                    exclude: currentSelection.exclude,
                  })
                }}
                className="flex items-center justify-center"
              >
                <X className={cn('h-3 w-3', chip.exclude ? 'text-[#F97066]' : 'text-[#9E77ED]')} />
              </button>
            </span>
          ))}
        </div>
      )}
    </>
  )

  if (bare) {
    return (
      <div className={cn('inline-flex flex-wrap items-center gap-2', className)}>
        <FilterDropdown
          title="Locations"
          placeholder="All Locations"
          options={locationOptions}
          selection={filters.location}
          showExclude
          disabled={disabled}
          pluralLabel="locations"
          onApply={(selection) => updateSelection('location', selection)}
        />
        <FilterDropdown
          title="Categories"
          placeholder="All Categories"
          options={categoryOptions}
          selection={filters.category}
          showExclude
          disabled={disabled}
          pluralLabel="categories"
          onApply={(selection) => updateSelection('category', selection)}
        />
        <FilterDropdown
          title="Classes"
          placeholder="All Classes"
          options={scopedClassOptions}
          selection={filters.class}
          showExclude
          disabled={disabled}
          pluralLabel="classes"
          onApply={(selection) => updateSelection('class', selection)}
        />
        {disabled && (
          <DisabledFiltersTooltip tooltipText={activeDisabledTooltip} />
        )}
        {hasActiveDashboardFilters(filters) && (
          <button
            type="button"
            onClick={() => onChange(createEmptyDashboardFilters())}
            className="text-xs font-medium text-[#667085] hover:text-[#101828]"
          >
            Reset
          </button>
        )}
      </div>
    )
  }

  return (
    fullBleed ? (
      <div className={cn('border-b border-[#EAECF0] bg-white', className)} style={fullBleedFilterBarStyle}>
        <div className="mx-auto box-border w-[95%] py-3">
          {content}
        </div>
      </div>
    ) : (
      <div className={cn('rounded-2xl border border-[#EAECF0] bg-white px-4 py-3 shadow-sm sm:px-6', className)}>
        {content}
      </div>
    )
  )
}

